import React, { useMemo } from 'react'
import { Box, Typography, Theme, Divider } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { PricingAndMaterialDetails } from '@src/aem-content/aem-types'
import { Link } from '@src/components/Link'
import { productDetailRoute } from '@src/routes'
import DiscoPromoProductMobilePriceForm from './DiscoPromoProductMobilePriceForm'

type DiscoPromoMobileCardProps = {
  discoPromoProductsSorted: PricingAndMaterialDetails[]
}

const useStyles = makeStyles((theme: Theme) => ({
  cardContainer: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    alignSelf: 'stretch',
    padding: theme.spacing(4, 0),
    background: theme.palette.background.default,
  },
  productInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
  productNumberText: {
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: '20px',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  productNameText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: '20px',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
  descriptionText: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: '20px',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  fullWidthDivider: {
    margin: theme.spacing(0, -5),
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
  },
}))

const DiscoPromoMobileCard: React.FC<DiscoPromoMobileCardProps> = ({
  discoPromoProductsSorted,
}) => {
  const classes = useStyles()

  const normalizeProducts = (
    products: PricingAndMaterialDetails[]
  ): PricingAndMaterialDetails[] => {
    return products.flatMap((product) => {
      if (product.items && product.items.length > 1) {
        return product.items.map((item) => ({
          ...product,
          productsku: item.materialNumber,
          materialNumber: item.materialNumber,
          items: [item],
        }))
      }
      return product
    })
  }

  const normalizedDiscoPromoProducts = useMemo(
    () => normalizeProducts(discoPromoProductsSorted),
    [discoPromoProductsSorted]
  )

  const productInfoContent = (
    promoProduct: PricingAndMaterialDetails,
    content: string,
    className: string
  ) => {
    if (promoProduct?.brand?.key && promoProduct?.product) {
      return (
        <Link
          {...productDetailRoute.index(
            promoProduct.brand.key,
            promoProduct.product
          )}
        >
          <Typography
            component="span"
            className={className}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </Link>
      )
    }
    return (
      <Typography
        component="span"
        className={className}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    )
  }

  return (
    <Box>
      <Divider className={classes.fullWidthDivider} />
      {normalizedDiscoPromoProducts?.map((promoProduct, index) => (
        <React.Fragment
          key={`${promoProduct.productsku || promoProduct.product}-${index}`}
        >
          <Box className={classes.card}>
            <Box className={classes.productInfo}>
              {productInfoContent(
                promoProduct,
                promoProduct?.productsku ?? '',
                classes.productNumberText
              )}
              {productInfoContent(
                promoProduct,
                promoProduct?.productName ?? '',
                classes.productNameText
              )}
              {productInfoContent(
                promoProduct,
                promoProduct?.productDescription ?? '',
                classes.descriptionText
              )}
            </Box>
            <DiscoPromoProductMobilePriceForm
              product={promoProduct.product}
              materialPricing={promoProduct?.items}
              dchainMessage={promoProduct?.dchainMessage}
            />
          </Box>
          <Divider className={classes.fullWidthDivider} />
        </React.Fragment>
      ))}
    </Box>
  )
}

export default DiscoPromoMobileCard
